import { PageShell } from "../../components/service/PageShell";
import { ServiceHero, CtaPrimary, CtaGhost, FeatureGrid, StepsList, CompareTable, FaqBlock, Section, Overline, SectionTitle, Accent, Reveal } from "../../components/service/Blocks";
import { LeadForm } from "../../components/LeadForm";
import { waLink } from "../../data/site";
import { ShieldCheck, CalendarCheck, Car, UserCheck, Gavel, BadgePercent, LifeBuoy, Camera, Check, Gift } from "lucide-react";

const WA_MSG = "Oi, eu quero a promoção Green Friday de 100% de cashback na primeira mensalidade do seguro";

const PLANS = [
  { name: "Basic", tag: "Roubo e furto", desc: "O essencial para não perder o veículo.", items: ["Roubo e furto", "Perda total", "Incêndio", "Proteção de R$ 10 mil (vida ou acidentes pessoais)"], optional: ["Colisão", "Assistência 24h"] },
  { name: "Parcial", tag: "Danos ao veículo", desc: "Para quem quer cobertura de batida, incêndio e enchente.", items: ["Roubo e furto", "Colisão", "Perda total", "Perda parcial", "Incêndio", "Enchente, granizo e queda de árvore"], optional: ["Assistência 24h", "Proteção de R$ 10 mil"] },
  { name: "Premium", tag: "Cobertura mais completa", desc: "Roubo, furto, colisão, natureza e assistência 24h juntos.", items: ["Roubo e furto", "Colisão", "Perda total", "Perda parcial", "Incêndio", "Enchente, granizo e queda de árvore", "Assistência 24h (guincho até 1.000 km)", "Proteção de R$ 10 mil (vida ou acidentes pessoais)"], optional: [] },
];

const FAQ = [
  { q: "É seguro de verdade ou proteção veicular?", a: "É seguro de verdade, regulado pela SUSEP, com apólice e coberturas definidas em contrato. Proteção veicular é outra coisa." },
  { q: "Tem fidelidade?", a: "Não. O seguro renova mês a mês e você pode cancelar quando quiser, seguindo o procedimento de cancelamento." },
  { q: "Motorista de aplicativo e taxista podem contratar?", a: "Sim. Basta informar na cotação que o veículo é usado para aplicativo ou táxi." },
  { q: "O carro precisa estar no meu nome?", a: "Não necessariamente. E qualquer pessoa habilitada pode dirigir, respeitando as condições da contratação." },
  { q: "Aceita carro de leilão?", a: "Sim, desde que esteja legalizado e seja aprovado na vistoria." },
  { q: "Tem franquia?", a: "Só em perda parcial (conserto). Em roubo, furto sem recuperação e perda total, não há franquia, e danos a terceiros também não têm." },
  { q: "Quanto recebo se o carro for roubado?", a: "Até 100% da Tabela FIPE do dia do sinistro, conforme as condições da apólice, em até 30 dias depois da documentação completa." },
  { q: "Quando começa a cobertura?", a: "Depois que a vistoria é aprovada e a placa fica ativa, a partir da 0h. O guincho fica disponível a partir do primeiro dia útil depois da ativação." },
  { q: "E se eu atrasar a mensalidade?", a: "A cobertura pode ser suspensa automaticamente até o pagamento. Por isso vale manter a recorrência em dia." },
];

export default function Seguros() {
  return (
    <PageShell testid="page-seguros">
      <ServiceHero
        testid="seguros-hero"
        overline="iGreen Seguros · carro e moto"
        title={["Cansado de ter o seguro negado", "ou caro demais?"]}
        sub="Seguro de carro e moto regulado pela SUSEP, no mês a mês e sem fidelidade. Aceita motorista de aplicativo, táxi e veículo que não está no seu nome. Cotação em poucos minutos e vistoria pelo celular."
        bullets={["Regulado pela SUSEP", "Mensal, sem fidelidade", "Aceita motorista de app", "Vistoria pelo celular"]}
        primary={<CtaPrimary href="#planos" external={false} testid="seguros-quote-cta">Fazer minha cotação grátis</CtaPrimary>}
        secondary={<CtaGhost href={waLink(WA_MSG)} testid="seguros-whatsapp-cta">Falar no WhatsApp</CtaGhost>}
      />

      <Section className="bg-[#061A14]" testid="seguros-promo">
        <Reveal>
          <div className="rounded-3xl border border-[#A3E635]/30 bg-[#0B261E]/60 backdrop-blur-md p-8 sm:p-12 grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <p className="text-xs font-mono2 uppercase tracking-[0.25em] text-[#A3E635] mb-4">Green Friday · Seguros · só até 30/11</p>
              <SectionTitle className="max-w-xl"><Accent>100% de cashback</Accent> na primeira mensalidade do seu seguro</SectionTitle>
              <p className="mt-4 max-w-xl text-sm sm:text-base text-emerald-100/60 leading-relaxed">
                A primeira mensalidade do seu seguro de <strong className="text-white">carro ou moto</strong> é por conta da iGreen. Seguro mensal, sem fidelidade.
              </p>
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-emerald-100/60">
                {["Carro e moto", "Mensal, sem fidelidade", "Regulado pela SUSEP"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5"><Check size={13} className="text-[#A3E635]" /> {t}</span>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <CtaPrimary href={waLink(WA_MSG)} testid="seguros-cashback-cta"><Gift size={16} /> Quero o meu 100% de cashback</CtaPrimary>
                <CtaGhost href="#planos" external={false} testid="seguros-see-plans-cta">Ver os planos</CtaGhost>
              </div>
              <p className="mt-5 text-[11px] text-emerald-100/40">Campanha válida até 30/11. Condições da campanha com o nosso atendimento.</p>
            </div>
            <div className="lg:col-span-2 text-center">
              <p className="font-heading text-[5rem] sm:text-[7rem] font-extrabold text-[#A3E635] leading-none text-glow">100%</p>
              <p className="text-sm text-emerald-100/60 font-semibold">de cashback na 1ª mensalidade</p>
            </div>
          </div>
        </Reveal>
      </Section>

      <Section testid="seguros-por-que">
        <Reveal>
          <Overline>Por que escolher</Overline>
          <SectionTitle className="max-w-3xl">O seguro feito para quem <Accent>foi recusado ou acha caro</Accent>.</SectionTitle>
          <p className="mt-4 max-w-2xl text-sm sm:text-base text-emerald-100/55">Seguro de carro e moto que cabe no mês, com regras claras e sem prender você por um ano.</p>
        </Reveal>
        <div className="mt-12">
          <FeatureGrid
            testid="seguros-features"
            items={[
              { icon: ShieldCheck, title: "Seguro de verdade", text: "Seguro regulado pela SUSEP, com apólice e coberturas definidas em contrato. Não é proteção veicular." },
              { icon: CalendarCheck, title: "Mensal e sem fidelidade", text: "Você paga mês a mês, como uma assinatura, sem comprometer o limite do cartão e sem contrato de 12 meses." },
              { icon: Car, title: "Motorista de app e táxi", text: "Uber, 99, iFood, táxi e carro de locadora são aceitos. É só informar o uso na cotação." },
              { icon: UserCheck, title: "Não precisa estar no seu nome", text: "O veículo não precisa estar no nome de quem contrata, e qualquer pessoa habilitada pode dirigir." },
              { icon: Gavel, title: "Carro de leilão também", text: "Veículos de leilão são aceitos, desde que legalizados e aprovados na vistoria." },
              { icon: BadgePercent, title: "Até 100% da FIPE", text: "Em roubo, furto ou perda total, a indenização segue a Tabela FIPE do dia do sinistro, sem franquia." },
              { icon: LifeBuoy, title: "Assistência 24h no Brasil todo", text: "Guincho, socorro mecânico, pane seca, troca de pneu e chaveiro, a qualquer hora." },
              { icon: Camera, title: "Vistoria pelo celular", text: "Sem ir a lugar nenhum: você fotografa o veículo pelo link e acompanha tudo pelo WhatsApp." },
            ]}
          />
        </div>
      </Section>

      <Section className="bg-[#061A14]" testid="seguros-planos">
        <div id="planos">
          <Reveal>
            <Overline>Planos e coberturas</Overline>
            <SectionTitle>Escolha a proteção <Accent>do seu tamanho</Accent>.</SectionTitle>
            <p className="mt-4 max-w-2xl text-sm sm:text-base text-emerald-100/55">
              Três planos para o carro. O valor sai na cotação, conforme o modelo, o ano e o uso do veículo.
            </p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {PLANS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.1}>
                <div
                  data-testid={`seguros-plan-${p.name.toLowerCase()}`}
                  className={`h-full rounded-2xl border p-7 flex flex-col transition-[border-color,transform] duration-300 hover:-translate-y-1.5 ${
                    p.name === "Premium" ? "border-[#00E676]/50 bg-[#0B261E]/80 shadow-[0_0_30px_rgba(0,230,118,0.12)]" : "border-white/10 bg-[#0B261E]/50"
                  }`}
                >
                  <span className="text-[10px] font-mono2 uppercase tracking-widest text-[#A3E635] border border-[#A3E635]/30 rounded-full px-3 py-1 w-fit">{p.tag}</span>
                  <h3 className="mt-4 font-heading text-2xl font-extrabold text-white">Plano {p.name}</h3>
                  <p className="mt-1.5 text-xs text-emerald-100/50">{p.desc}</p>
                  <ul className="mt-5 space-y-2 text-xs text-emerald-100/65 flex-1">
                    {p.items.map((it) => (
                      <li key={it} className="flex gap-2 items-start"><Check size={13} className="text-[#00E676] mt-0.5 shrink-0" />{it}</li>
                    ))}
                    {p.optional.map((it) => (
                      <li key={it} className="flex gap-2 items-start text-emerald-100/35"><span className="text-[10px] mt-0.5 shrink-0 border border-white/20 rounded px-1">OPCIONAL</span>{it}</li>
                    ))}
                  </ul>
                  <a
                    href={waLink(WA_MSG)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`seguros-plan-${p.name.toLowerCase()}-cta`}
                    className="mt-6 inline-flex justify-center rounded-full bg-[#00E676] hover:bg-[#00C853] text-[#04120E] text-sm font-bold px-5 py-3 transition-colors duration-300"
                  >
                    Cotar o plano {p.name}
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 rounded-2xl border border-white/10 bg-[#0B261E]/50 p-7">
              <p className="font-heading font-bold text-white">Turbine qualquer plano com opcionais:</p>
              <div className="mt-4 grid sm:grid-cols-3 gap-4 text-xs text-emerald-100/60 leading-relaxed">
                <p><strong className="text-white">Danos a terceiros</strong> · Até R$ 50 mil, R$ 100 mil ou R$ 150 mil, sem franquia.</p>
                <p><strong className="text-white">Carro reserva</strong> · 7, 15 ou 30 dias enquanto o seu carro estiver no conserto.</p>
                <p><strong className="text-white">Vidros</strong> · Só o para-brisa ou vidros completos, com faróis, lanternas e retrovisores.</p>
              </div>
            </div>
            <p className="mt-5 text-[11px] text-emerald-100/40 leading-relaxed max-w-3xl">
              Coberturas conforme as condições do seguro. Em perda parcial há franquia; em roubo, furto e perda total, não.
              Perda total é quando o dano chega a 75% do valor do veículo. Também fazemos seguro de moto: os planos da moto aparecem na cotação.
            </p>
          </Reveal>
        </div>
      </Section>

      <Section testid="seguros-apps">
        <Reveal>
          <Overline>Para quem trabalha rodando</Overline>
          <SectionTitle className="max-w-3xl">Motorista de Uber, 99 ou iFood? <Accent>Aqui você é bem-vindo.</Accent></SectionTitle>
          <p className="mt-4 max-w-2xl text-sm sm:text-base text-emerald-100/55 leading-relaxed">
            Muita seguradora recusa ou encarece o seguro de quem trabalha com o carro ou a moto. Aqui, motorista de
            aplicativo, entregador e taxista são aceitos: é só informar o uso na cotação.
          </p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            ["Aceito de verdade", "O uso para aplicativo entra na cotação, sem surpresa depois."],
            ["Cabe no seu mês", "Mensalidade que acompanha o caixa de quem roda todo dia."],
            ["Assistência enquanto trabalha", "Guincho e socorro 24h para não perder o dia (no Premium)."],
            ["Indenização em até 30 dias", "Depois da documentação completa, mesmo usando o veículo para trabalhar."],
          ].map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.07}>
              <div className="rounded-2xl border border-white/10 bg-[#0B261E]/60 p-6 h-full">
                <p className="font-heading font-bold text-white text-sm">{t}</p>
                <p className="mt-2 text-xs text-emerald-100/55 leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-[11px] text-emerald-100/35 max-w-3xl leading-relaxed">
          Uber, 99 e iFood são marcas dos seus donos, citadas só para identificar quem trabalha com esses aplicativos. A iGreen Seguros não tem parceria com essas empresas.
        </p>
      </Section>

      <Section className="bg-[#061A14]" testid="seguros-compare">
        <Reveal>
          <Overline>Compare</Overline>
          <SectionTitle>iGreen Seguros <Accent>x seguro tradicional</Accent>.</SectionTitle>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12">
            <CompareTable
              testid="seguros-compare-table"
              leftName="iGreen Seguros"
              rightName="Seguro tradicional"
              rows={[
                ["Pagamento", "Mensal, como assinatura", "Anual ou parcelado no cartão"],
                ["Fidelidade", "Nenhuma", "Contrato de 12 meses"],
                ["Motorista de app e táxi", "Aceito", "Muitas vezes recusado"],
                ["Carro de leilão", "Aceito, se legalizado e vistoriado", "Raramente aceito"],
                ["Veículo em nome de outra pessoa", "Pode", "Geralmente não"],
                ["Contratação e vistoria", "Pelo celular", "Corretor, papelada e agendamento"],
                ["Limite do cartão", "Não compromete", "Compromete o valor do ano"],
              ]}
              note="Comparação com condições comuns no mercado de seguro auto; cada seguradora tem as suas regras."
            />
          </div>
        </Reveal>
      </Section>

      <Section testid="seguros-passos">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <Overline>Passo a passo</Overline>
            <SectionTitle>Seu veículo protegido em <Accent>5 passos</Accent></SectionTitle>
          </Reveal>
          <StepsList
            testid="seguros-steps"
            steps={[
              { n: "01", title: "Faça a cotação", text: "Modelo, ano e uso do veículo. Leva poucos minutos e não tem compromisso." },
              { n: "02", title: "Escolha o plano", text: "Basic, Parcial ou Premium, mais os opcionais que fizerem sentido." },
              { n: "03", title: "Pague a 1ª mensalidade", text: "Ela libera a vistoria. Se o seguro for recusado, o valor pode ser estornado." },
              { n: "04", title: "Vistoria pelo celular", text: "Você fotografa o veículo pelo link que chega no WhatsApp." },
              { n: "05", title: "Seguro ativo", text: "Vistoria aprovada, a cobertura começa à 0h com a placa ativa." },
            ]}
          />
        </div>
      </Section>

      <FaqBlock testid="seguros-faq" title={<>Perguntas sobre <Accent>seguro auto e moto</Accent></>} items={FAQ} />

      <LeadForm
        overline="Vamos conversar?"
        title={<>Me conta o seu caso e eu te respondo <Accent>no WhatsApp</Accent>.</>}
        defaultInterest="Seguro de carro ou moto"
      />
    </PageShell>
  );
}
